import { useEffect, useState } from "react";
import { FlaskConical, Trash2, ExternalLink, X, RotateCcw } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr } from "../lib/api";

// Master-only: create / open / delete the "Demo Co (TEST)" client used for demos and testing.
export function DemoCompanyButton({ onChanged }) {
  const [st, setSt] = useState(null);
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/demo-company").then((r) => setSt(r.data)).catch(() => setSt(null));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/demo-company", { password: pw });
      toast.success(data.created ? `Demo company created · ${data.employees} employees · ${data.runs.length} posted runs · login ${data.login}` : "Demo company already exists");
      setOpen(false); setPw(""); load(); onChanged?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm("Reset the demo company? All demo data is wiped and re-seeded fresh (same login, new password below).")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/demo-company/reset", { password: pw });
      toast.success(`Demo reset · ${data.employees} employees · ${data.runs.length} posted runs`);
      setOpen(false); setPw(""); load(); onChanged?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("Delete the demo company and ALL of its data (employees, runs, payslips, time, logins)? Real clients are not affected.")) return;
    setBusy(true);
    try { await api.delete("/demo-company"); toast.success("Demo company deleted"); setOpen(false); load(); onChanged?.(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!st) return null;
  return (
    <>
      <button data-testid="demo-company-button" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 border border-dashed border-black px-4 py-2.5 text-xs font-mono uppercase tracking-widest hover:bg-black hover:text-white transition-colors">
        <FlaskConical className="h-3.5 w-3.5" /> {st.exists ? "Demo company" : "Create demo company"}
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="demo-company-dialog" onClick={() => setOpen(false)}>
          <div className="bg-white border border-black w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-bold tracking-tight flex items-center gap-2"><FlaskConical className="h-4 w-4" /> {st.name}</h3>
              <button onClick={() => setOpen(false)} aria-label="Close" className="text-muted-foreground hover:text-black"><X className="h-4 w-4" /></button>
            </div>
            {st.exists ? (
              <>
                <p className="text-sm text-muted-foreground">The demo client exists. Client login: <span className="font-mono">{st.login}</span> (client admin). Company and employee emails use <span className="font-mono">{st.email}</span>. Scheduled reminders skip this company.</p>
                <label className="block">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">New password for {st.login} (required to reset)</span>
                  <input type="password" data-testid="demo-company-reset-password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" className="mt-1 w-full border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
                </label>
                <div className="flex flex-wrap gap-2">
                  <button data-testid="demo-company-reset" disabled={busy || pw.length < 8} onClick={reset} className="inline-flex items-center gap-1.5 bg-black text-white px-4 py-2 text-sm hover:bg-neutral-800 transition-colors disabled:opacity-50"><RotateCcw className="h-3.5 w-3.5" /> {busy ? "Resetting…" : "Reset demo"}</button>
                  <Link to={`/companies/${st.company_id}`} data-testid="demo-company-open" onClick={() => setOpen(false)} className="inline-flex items-center gap-1.5 border border-black px-4 py-2 text-sm hover:bg-neutral-100 transition-colors"><ExternalLink className="h-3.5 w-3.5" /> Open</Link>
                  <button data-testid="demo-company-delete" disabled={busy} onClick={remove} className="inline-flex items-center gap-1.5 border border-red-600 text-red-700 px-4 py-2 text-sm hover:bg-red-600 hover:text-white transition-colors disabled:opacity-50"><Trash2 className="h-3.5 w-3.5" /> {busy ? "Deleting…" : "Delete demo company"}</button>
                </div>
              </>
            ) : (
              <>
                <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
                  <li>Ontario, bi-weekly, flagged as test data (reminders & digests skip it)</li>
                  <li>5 synthetic employees (3 hourly, 2 salaried; one OT-exempt, one vacation payout)</li>
                  <li>2 posted historical runs + 2 approved weeks of time ready to run</li>
                  <li>Client admin login <span className="font-mono">{st.login}</span>; company &amp; employee emails <span className="font-mono">{st.email}</span></li>
                </ul>
                <label className="block">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Password for {st.login}</span>
                  <input type="password" data-testid="demo-company-password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" className="mt-1 w-full border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
                </label>
                <button data-testid="demo-company-create" disabled={busy || pw.length < 8} onClick={create} className="w-full bg-black text-white px-4 py-2.5 text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50">{busy ? "Creating… (runs 2 payrolls)" : "Create demo company"}</button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
